// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadCarouselProducts from "@insite/client-framework/Store/Components/ProductCarousel/Handlers/LoadCarouselProducts";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductDetailsScratchAndDentCarouselProduct, {
    ProductDetailsScratchAndDentCarouselProductStyles,
} from "@plumberssupply/App/Components/ProductDetails/ProductDetailsScratchAndDentCarouselProduct";
import XngCarousel, { SwiperStyle } from "@plumberssupply/App/Components/XngCarousel/XngCarousel";
import { getXngCommon } from "@plumberssupply/App/Store/Selectors/CommonSelectors";
import { color } from "@plumberssupply/XDL/Utilities";
import { hideElementOnPrint } from "@plumberssupply/XDL/Utilities";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { useCallback } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    title = "title",
    description = "description",
    numberOfProductsToDisplay = "numberOfProductsToDisplay",
    maxNumberOfColumns = "maxNumberOfColumns",
    showImage = "showImage",
    showTitle = "showTitle",
    showBrandName = "showBrandName",
    showPartNumbers = "showPartNumbers",
    showLocation = "showLocation",
    showPrice = "showPrice",
    showCondition = "showCondition",
    showContact = "showContact",
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
    upperBorder = "upperBorder",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
        [fields.numberOfProductsToDisplay]: number;
        [fields.maxNumberOfColumns]: number;
        [fields.showImage]: boolean;
        [fields.showTitle]: boolean;
        [fields.showBrandName]: boolean;
        [fields.showPartNumbers]: boolean;
        [fields.showLocation]: boolean;
        [fields.showPrice]: boolean;
        [fields.showCondition]: boolean;
        [fields.showContact]: boolean;
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
        [fields.upperBorder]: boolean;
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    products: getProductsForProductInfoList(state, ownProps.id),
    errorMessage: getErrorMessage(state, ownProps.id),
    pageType: getCurrentPage(state).type,
    isMobileScreen: getXngCommon(state).isMobileScreen,
});

const mapDispatchToProps = {
    loadCarouselProducts,
};

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext;

export interface ProductDetailsScratchAndDentStyles {
    wrapper?: InjectableCss;
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    descriptionText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    swiperStyles?: SwiperStyle;
    carouselProductStyles?: ProductDetailsScratchAndDentCarouselProductStyles;
}

export const productDetailsScratchAndDentStyles: ProductDetailsScratchAndDentStyles = {
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        color: "common.ui11",
        css: css`
            margin-bottom: 16px;
        `,
    },
    descriptionText: {
        css: css`
            display: block;
            margin-bottom: 24px;
            line-height: 26px;
        `,
    },
    wrapper: {
        css: css`
            ${({ showUpperBorder }: any) =>
                showUpperBorder &&
                css`
                    padding: 32px 0 22px;
                    border-top: 1px solid ${color("common.ui7")};
                `}
            ${hideElementOnPrint}
        `,
    },
    swiperStyles: {
        container: {
            css: css`
                .xng-pagination-container {
                    margin-top: 10px;
                }
            `,
        },
    },
};

const styles = productDetailsScratchAndDentStyles;

const ProductDetailsScratchAndDentCarousel: React.FC<Props> = ({
    id,
    theme,
    fields,
    products,
    errorMessage,
    loadCarouselProducts,
    pageType,
    shellContext,
    isMobileScreen,
}) => {
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const isProductDetailsPage = pageType === "ProductDetailsPage";
    const isProductListPage = pageType === "ProductListPage";
    const isBrandDetailsPage = pageType === "BrandDetailsPage";
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);
    const productContext = React.useContext(ProductContext);
    const [scrollOnce, setScrollOnce] = React.useState(true);
    const carouselType = "scratch & dent";

    React.useEffect(() => {
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId: id,
            carouselType,
            relatedProductType: carouselType,
            seedWithManuallyAssigned: "",
            displayProductsFrom: "",
            selectedCategoryIds: [],
            numberOfProductsToDisplay: fields.numberOfProductsToDisplay,
            isProductDetailsPage,
            productId: productContext?.product?.id,
            isProductListPage,
            category,
            isBrandDetailsPage,
            brand,
        });
    }, [fields.numberOfProductsToDisplay, productContext?.product?.id, category?.id, brand?.id]);

    const handleScroll = () => {
        const queryParams = parseQueryString<{ ScratchAndDent?: string }>(window.location.search);
        const scratchAndDentProductsCarousel = document.querySelector(
            "#productCarousel_scratchAndDent",
        ) as HTMLElement | null;
        if (queryParams?.ScratchAndDent === "" && scratchAndDentProductsCarousel) {
            window.scrollTo({
                top: scratchAndDentProductsCarousel?.offsetTop + (isMobileScreen ? 100 : -150),
                left: 0,
                behavior: "smooth",
            });
            setScrollOnce(false);
        }
    };

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);

    const getSlidesToScroll = useCallback(() => {
        const maxNumberOfColumns = fields.maxNumberOfColumns || 4;
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        const { innerWidth } = window;
        if (innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 1;
        } else if (innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 3;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, [fields.maxNumberOfColumns]);

    const [slidesToScroll, setSlidesToScroll] = React.useState(getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());

    React.useEffect(() => {
        const newSlidesToScroll = getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [fields.maxNumberOfColumns, windowResizeTime]);

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        embla.reInit({
            align: "start",
            slidesToScroll,
            draggable,
            loop: products.length > slidesToScroll,
        });
    }, [embla, slidesToScroll, draggable, products]);

    React.useEffect(() => {
        if (!products || products.length === 0) {
            return;
        }
        if (scrollOnce && products && products.length) {
            handleScroll();
        }
    }, [products]);

    const renderProductItem = (product: ProductModel) => {
        return (
            <ProductDetailsScratchAndDentCarouselProduct
                key={product.id}
                carouselId={id}
                product={product}
                showImage={fields.showImage}
                showLocation={fields.showLocation}
                showPrice={fields.showPrice}
                showCondition={fields.showCondition}
                showContact={fields.showContact}
                showAddToCart={fields.showAddToCart}
                extendedStyles={styles.carouselProductStyles}
            />
        );
    };

    if (errorMessage && shellContext.isInShell) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    const title = fields.title || translate("Scratch & Dent");
    const showUpperBorder = fields.upperBorder;

    // XNG-Changes: use XngCarousel
    return (
        <>
            <SkipNav
                text={translate("Skip Carousel")}
                extendedStyles={styles.skipCarouselButton}
                destination={afterCarousel}
            />
            <StyledWrapper
                data-test-selector="productCarousel_scratchAndDent"
                id="productCarousel_scratchAndDent"
                {...styles.wrapper}
                {...({ showUpperBorder } as any)}
                ref={emblaRef}
            >
                <Typography {...styles.titleText}>{title}</Typography>
                {fields.description && (
                    <Typography
                        {...styles.descriptionText}
                        dangerouslySetInnerHTML={{ __html: fields.description }}
                    ></Typography>
                )}
                <XngCarousel
                    navigation={true}
                    slidesPerView={slidesToScroll}
                    spaceBetween={50}
                    centerInsufficientSlides={false}
                    extendedStyles={styles.swiperStyles}
                >
                    {products.map(product => renderProductItem(product))}
                </XngCarousel>
            </StyledWrapper>
            <span ref={afterCarousel} tabIndex={-1} />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(
        mapStateToProps,
        mapDispatchToProps,
    )(withIsInShell(withTheme(ProductDetailsScratchAndDentCarousel))),
    definition: {
        group: "Xngage Custom" as any,
        icon: "gallery-thumbnails",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                sortOrder: 0,
            },
            {
                name: fields.description,
                displayName: "Description",
                editorTemplate: "RichTextField",
                defaultValue: "<p>Keep up-to-date on product news and the latest offers.</p>",
                fieldType: "Translatable",
                sortOrder: 1,
            },
            {
                name: fields.numberOfProductsToDisplay,
                displayName: "Number Of Products To Display",
                editorTemplate: "IntegerField",
                min: 1,
                max: 999,
                defaultValue: 10,
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.maxNumberOfColumns,
                displayName: "Max Number Of Columns",
                editorTemplate: "IntegerField",
                min: 1,
                max: 4,
                defaultValue: 3,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showImage,
                displayName: "Image",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                isEnabled: () => false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showLocation,
                displayName: "Location",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.showPrice,
                displayName: "Price",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 6,
            },
            {
                name: fields.showCondition,
                displayName: "Condition",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 7,
            },
            {
                name: fields.showContact,
                displayName: "Contact",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 8,
            },
            {
                name: fields.showAddToCart,
                displayName: "Add to Cart",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 9,
            },
            {
                name: fields.upperBorder,
                displayName: "Upper Border",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 10,
            },
        ],
    },
};

export default widgetModule;
